import React from 'react';
import PropTypes from 'prop-types';
import * as Buttons from './button.styled';

/**
 * Map variations to Components so that we can
 * use dynamic JSX tags.
 */
export const componentsMap = {
  primary: Buttons.PrimaryButton,
  heavy: Buttons.HeavyButton,
  secondary: Buttons.SecondaryButton,
  flat: Buttons.FlatButton,
  primaryIcon: Buttons.PrimaryIcon,
  secondaryIcon: Buttons.SecondaryIcon,
  flatIcon: Buttons.FlatIcon,
};

/**
 * Gets the button variation
 *
 * @param {string} icon The icon filename in PascalCase
 * @param {string} variation The variation of the button
 *
 * @throws {Error} Catch me with an error boundary
 *
 * @returns {string} `variationIcon` or `variation`
 */
export const getButtonVariation = (icon, variation) => {
  if (icon && variation === 'heavy') {
    throw new Error("Icon & Heavy can't be used together");
  }

  if (icon) {
    return `${variation}Icon`;
  }

  return variation;
};

/**
 * Gets the behavior of the tag.
 * Either `a` or `button`
 *
 * @param {string} href
 *
 * @returns {string}
 */
export const getTagBehavior = (href) => {
  if (href) {
    return 'a';
  }

  return 'button';
};

/**
 * Button is a fully controlled functional component that will
 * render a dynamic JSX tag based on properties passed in to it.
 * The final render will be either a `button` tag or an `a` tag.
 */
function Button(props) {
  const {
    id,
    disabled,
    href,
    icon,
    onClick,
    target,
    text,
    variation,
    ...rest
  } = props;

  // If we have an unknown variation render the default
  if (typeof componentsMap[variation] === 'undefined') {
    return (
      <Buttons.PrimaryButton
        id={id}
        onClick={onClick}
        disabled={disabled}
        {...rest}
      >
        {text}
      </Buttons.PrimaryButton>
    );
  }

  // Set the icon component
  const IconComponent = icon || null;
  // Get the button type based on props (primary,primaryIcon, etc)
  const buttonVariation = getButtonVariation(icon, variation);
  // Get the tag behavior (a, button)
  const tagBehavior = getTagBehavior(href);
  // set disabled state based on tag behavior
  const disable = !!(tagBehavior === 'button' && disabled);
  // set the Tag from the componentsMap object
  const Tag = componentsMap[buttonVariation];
  const opts =
    tagBehavior === 'a'
      ? { href, onClick, target }
      : { onClick, disabled: disable };

  return (
    <Tag as={tagBehavior} id={id} {...opts} aria-label={text} {...rest}>
      {IconComponent ? (
        <IconComponent id={`${id}-icon`} />
      ) : (
        <span>{text}</span>
      )}
    </Tag>
  );
}

/**
 * propTypes for this component
 */
Button.propTypes = {
  id: PropTypes.string,
  /** Disabled:
   *  Indicates if the button should be disabled.
   *  This should be controlled via the onClick prop
   */
  disabled: PropTypes.bool,
  /** href to link to:
   * Setting this will produce an anchor tag
   */
  href: PropTypes.string,
  /** The icon component to use */
  icon: PropTypes.func,
  /** Click handler */
  onClick: PropTypes.func,
  /** Link Target:
   *  When requesting a link what window to open a link in
   */
  target: PropTypes.string,
  /** Button Text */
  text: PropTypes.string,
  /** Button Variation:
   */
  variation: PropTypes.oneOf(['primary', 'secondary', 'heavy', 'flat']),
};

/**
 * Default prop types
 */
Button.defaultProps = {
  id: '',
  disabled: false,
  href: '',
  icon: null,
  target: '_blank',
  text: 'submit',
  variation: 'primary',
  onClick: () => true,
};

/* @component */
export default Button;
